//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import angular, { IScope } from 'angular';
import React, { useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import {
  ModeratorBulkMediaAction,
  ModeratorBulkScope,
  hasCurrentModeratorBulkWriteAccess,
  useModeratorBulkMediaMutation,
} from '@app/bulk-actions/hooks/useModeratorBulkMediaMutation';
import { BulkActionNames } from '@app/bulk-actions/model/BulkActionNames';
import { getModeratorBulkActionsAccess } from '@app/bulk-actions/model/moderatorBulkActionsAccess';
import { getBulkActionsSelectedItems } from '@app/bulk-actions/selectors/bulk-actions-selector';
import { UiaBulkActionsTrackingIds } from '@app/bulk-actions/uia-bulk-actions/uiaBulkActionsTrackingIds';
import Icon from '@components/atoms/icon/Icon';
import ActionSheet from '@components/molecules/action-sheet/ActionSheet';
import { Modal } from '@components/molecules/modal/Modal';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { MediaLibraryQueryParams } from '@media-library/constants/mediaLibraryConstants';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import { Permissions } from '@models/Permissions';
import { useModeratorSurface } from '@moderation-mode/hooks/useModeratorSurface';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { getClientId } from '@redux/selectors/getClientId';
import { pluralize } from '@utils/stringUtils';
import { withTracking } from '@utils/withTracking';

interface Props {
  downloadDisabledReason: false | string;
  onDownload: () => void;
}

type ConfirmationAction = 'remove' | 'request-review';

const NO_PERMISSION_MESSAGE =
  "You don't have permission to perform this action.";

export const ModeratorBulkActions = ({
  downloadDisabledReason,
  onDownload,
}: Props) => {
  const session = useSelector((store: ICuralateStore) => store.session);
  const clientId = useSelector(getClientId);
  const selectedItems = useSelector(getBulkActionsSelectedItems);
  const currentCollection = useSelector(
    (store: ICuralateStore) => store.bulkActions.currentCollection,
  );
  const disabledActions = useSelector(
    (store: ICuralateStore) => store.bulkActions.disabledActions,
  );
  const { params, state } = useCurrentStateAndParams<MediaLibraryQueryParams>();
  const { isModeratorSurface } = useModeratorSurface();
  const access = getModeratorBulkActionsAccess(
    session,
    isModeratorSurface,
    state,
  );
  const mutation = useModeratorBulkMediaMutation();
  const store = useStore<ICuralateStore>();
  const submittingRef = useRef(false);
  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction | null>(null);

  if (!access.isEnabled || selectedItems.length === 0) {
    return null;
  }

  const scope: ModeratorBulkScope = {
    clientId,
    stateName: state,
    collectionId: params.collectionId,
  };
  const itemCount = selectedItems.length;
  const items = pluralize(itemCount, 'item');
  const isStreamRemovalUnavailable =
    access.isStream &&
    (currentCollection?.id !== params.collectionId ||
      disabledActions.includes(BulkActionNames.REMOVE_FROM_COLLECTION));
  const isWriteDisabled = !access.canWrite || mutation.isPending;

  const submit = async (action: ModeratorBulkMediaAction) => {
    if (
      submittingRef.current ||
      mutation.hasPendingBulkAction() ||
      !hasCurrentModeratorBulkWriteAccess(scope, store.getState())
    ) {
      return;
    }

    submittingRef.current = true;
    try {
      await mutation.mutateAsync({
        action,
        scope,
        cuids: [
          ...new Set(
            selectedItems.map((item) => Cuid.fromPartial(item.cuid, clientId)),
          ),
        ],
      });
      setConfirmationAction(null);
    } catch {
      // The mutation shows the standard error toast; retain the confirmation
      // and selection so that a failed request can be retried.
    } finally {
      submittingRef.current = false;
    }
  };

  const openConfirmation = (action: ConfirmationAction) => {
    if (
      !isWriteDisabled &&
      hasCurrentModeratorBulkWriteAccess(scope, store.getState()) &&
      !(action === 'remove' && isStreamRemovalUnavailable)
    ) {
      setConfirmationAction(action);
    }
  };

  const confirm = () => {
    if (
      isWriteDisabled ||
      !hasCurrentModeratorBulkWriteAccess(scope, store.getState())
    ) {
      return;
    }

    if (confirmationAction === 'request-review') {
      void submit('request-review');
    } else if (confirmationAction === 'remove') {
      if (isStreamRemovalUnavailable) {
        return;
      }

      void submit(access.isAllSharedMedia ? 'unshare' : 'remove-from-stream');
    }
  };

  const download = () => {
    const liveState = store.getState();
    if (
      !isWriteDisabled &&
      !downloadDisabledReason &&
      hasCurrentModeratorBulkWriteAccess(scope, liveState) &&
      getBulkActionsSelectedItems(liveState).length > 0
    ) {
      onDownload();
    }
  };

  const getConfirmationMessage = () => {
    if (confirmationAction === 'request-review') {
      return access.isAllSharedMedia
        ? "The media you've selected has already been reviewed by the brand."
        : `Request review now for ${items}, including items without product tags?`;
    }
    if (access.isAllSharedMedia) {
      return `Remove ${items} from All Shared Media?`;
    }
    return `Remove ${items} from the ${currentCollection?.name} stream? This will not unshare the media.`;
  };

  return (
    <div className='uia-bulk-actions-icon-actions'>
      {access.isStream && (
        <SmallTooltip
          appendTarget='body'
          content={NO_PERMISSION_MESSAGE}
          isDisabled={access.canWrite}
          placement='bottom'
          target={
            <span>
              <TrackedToolbarButton
                aria-disabled={isWriteDisabled}
                className='cu-button-flat-primary bulk-actions-top-bar-button cu-mr-4'
                onClick={() => {
                  if (!isWriteDisabled) {
                    void submit('share');
                  }
                }}
                trackingId={UiaBulkActionsTrackingIds.MODERATOR_SHARE}
                type='button'
              >
                + Share {pluralize(itemCount, 'Item')}
              </TrackedToolbarButton>
            </span>
          }
        />
      )}
      <SmallTooltip
        appendTarget='body'
        content={NO_PERMISSION_MESSAGE}
        isDisabled={access.canWrite}
        placement='bottom'
        target={
          <span>
            <TrackedToolbarButton
              aria-disabled={isWriteDisabled || isStreamRemovalUnavailable}
              className='cu-button-flat-primary bulk-actions-top-bar-button cu-mr-4'
              onClick={() => openConfirmation('remove')}
              trackingId={UiaBulkActionsTrackingIds.MODERATOR_REMOVE}
              type='button'
            >
              Remove {itemCount} Media
            </TrackedToolbarButton>
          </span>
        }
      />
      <ActionSheet
        actionSheetConfig={[
          {
            isDisabled: isWriteDisabled,
            label: 'Request review now',
            onClick: () => openConfirmation('request-review'),
            tooltipContent: !access.canWrite
              ? NO_PERMISSION_MESSAGE
              : undefined,
            trackingId: UiaBulkActionsTrackingIds.MODERATOR_REQUEST_REVIEW,
          },
          {
            isDisabled: isWriteDisabled || !!downloadDisabledReason,
            label: 'Download',
            onClick: download,
            tooltipContent: !access.canWrite
              ? NO_PERMISSION_MESSAGE
              : downloadDisabledReason || undefined,
            trackingId: UiaBulkActionsTrackingIds.MODERATOR_DOWNLOAD,
          },
        ]}
        appendTarget='body'
        placement='bottom-end'
      >
        <TrackedToolbarButton
          aria-label='More bulk actions'
          className='cu-button-invisible cu-py-2 cu-d-inline-block uia-more-button'
          disabled={mutation.isPending}
          trackingId={UiaBulkActionsTrackingIds.MODERATOR_MORE}
          type='button'
        >
          <Icon className='cu-rot-90' name='more' />
        </TrackedToolbarButton>
      </ActionSheet>
      <Modal
        closeModal={
          mutation.isPending ? undefined : () => setConfirmationAction(null)
        }
        contents={<p>{getConfirmationMessage()}</p>}
        isModalOpen={confirmationAction !== null}
        primaryButtonInfo={{
          buttonContent:
            confirmationAction === 'request-review'
              ? 'Request review now'
              : 'Remove',
          isDisabled:
            isWriteDisabled ||
            (confirmationAction === 'remove' && isStreamRemovalUnavailable),
          onClick: confirm,
          permissionName: Permissions.ModerationView,
          trackingId: UiaBulkActionsTrackingIds.MODERATOR_CONFIRM,
        }}
        secondaryButtonInfo={{
          buttonContent: 'Cancel',
          isDisabled: mutation.isPending,
          onClick: () => setConfirmationAction(null),
          trackingId: UiaBulkActionsTrackingIds.MODERATOR_CANCEL,
        }}
        title={
          confirmationAction === 'request-review'
            ? access.isAllSharedMedia
              ? 'Are You Sure?'
              : 'Request review now?'
            : `Remove ${itemCount} media?`
        }
        width='medium'
      />
    </div>
  );
};

const TrackedToolbarButton = withTracking('button');

angular.module('curalate').component('moderatorBulkActions', {
  bindings: {
    onDownload: '&',
    downloadDisabledReason: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: IScope & { $ctrl: Props }) => (
      <ModeratorBulkActions
        {...scope.$ctrl}
        onDownload={
          applyScope(scope, scope.$ctrl.onDownload) as Props['onDownload']
        }
      />
    ),
  ),
});
